import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import NoShowCountdown from '../../components/LeadsVo/NoShowCountdown';
import RdvStatusBadge from '../../components/LeadsVo/RdvStatusBadge';
import { useCancelRdv, useLeadsVoAgent, useLeadsVoRdvs, useReportNoShow } from '../../hooks/useLeadsVoQueries';
import { canCancelRdv, canReportNoShow, describeRdvActionError } from '../../utils/leadsVoRdvActions';

const headerStyle = {
  background: '#262626',
  color: 'white',
  fontWeight: 'bold',
  fontSize: 14,
  position: 'sticky' as const,
  top: 0,
  zIndex: 100,
  whiteSpace: 'nowrap' as const,
};

const cellStyle = {
  height: 55,
  verticalAlign: 'middle' as const,
  fontSize: 14,
  color: '#262626',
};

// L'URL d'annonce vient d'un service externe : on ne rend le lien que pour
// les schémas http(s) (bloque les URI javascript: et autres).
const safeHttpUrl = (url: string | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined;

const Rdvs = () => {
  // Tous les hooks DOIVENT être appelés inconditionnellement (Rules of Hooks).
  // Le early-return Navigate vient ensuite, après tous les useXxx.
  const { data: agentData, isLoading: agentLoading } = useLeadsVoAgent();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data, isLoading } = useLeadsVoRdvs(page + 1, rowsPerPage);
  const reportNoShow = useReportNoShow();
  const cancelRdv = useCancelRdv();

  const [noShowDialog, setNoShowDialog] = useState<any>(null);
  const [noShowMotif, setNoShowMotif] = useState('');
  const [cancelDialog, setCancelDialog] = useState<any>(null);
  const [cancelMotif, setCancelMotif] = useState('');
  const [cancelError, setCancelError] = useState('');

  if (!agentLoading && agentData?.partner?.statut !== 'active') {
    return <Navigate to="/leads-vo/onboarding" replace />;
  }

  const handleNoShow = async () => {
    if (!noShowDialog) return;
    await reportNoShow.mutateAsync({ rdvId: noShowDialog.id, motif: noShowMotif });
    setNoShowDialog(null);
    setNoShowMotif('');
  };

  // Le partenaire peut refuser (400, créneau écoulé…) : on affiche son motif dans la
  // fenêtre au lieu de la laisser ouverte sans explication.
  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      setCancelError('');
      await cancelRdv.mutateAsync({ rdvId: cancelDialog.id, motif: cancelMotif });
      setCancelDialog(null);
      setCancelMotif('');
    } catch (error) {
      setCancelError(describeRdvActionError(error));
    }
  };

  const canCancel = (rdv: any) => canCancelRdv(rdv);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626', mb: 3 }}>
        Mes Rendez-vous
      </Typography>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>Date</TableCell>
                <TableCell sx={headerStyle}>Lead</TableCell>
                <TableCell sx={headerStyle}>Véhicule</TableCell>
                <TableCell sx={headerStyle}>Téléphone</TableCell>
                <TableCell sx={headerStyle}>Statut</TableCell>
                <TableCell sx={headerStyle}>Délai contestation</TableCell>
                <TableCell sx={headerStyle} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.rdvs && data.rdvs.length > 0 ? (
                data.rdvs.map((rdv: any) => {
                  const nom = rdv.particulier?.nom ?? rdv.lead_name ?? '—';
                  const tel = rdv.particulier?.telephone ?? rdv.particulier?.phone ?? rdv.lead_phone ?? '—';
                  const v = rdv.vehicule;
                  const vehicule = v ? `${v.marque} ${v.modele} ${v.annee}` : rdv.lead_vehicle ?? '—';
                  const date = rdv.slot?.start ?? rdv.slot_start ?? rdv.scheduled_at;
                  const statut = rdv.statut ?? rdv.status;
                  const adUrl = safeHttpUrl(rdv.ad_url ?? rdv.lead_source_url);

                  return (
                  <TableRow key={rdv.id} hover sx={{ '&:hover': { backgroundColor: '#FAFAFA' } }}>
                    <TableCell sx={cellStyle}>
                      {date
                        ? new Date(date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>{nom}</TableCell>
                    <TableCell sx={cellStyle}>
                      {adUrl ? (
                        <a
                          href={adUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#262626', textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                          {vehicule} ↗
                        </a>
                      ) : (
                        vehicule
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>{tel}</TableCell>
                    <TableCell sx={cellStyle}>
                      <RdvStatusBadge status={statut} />
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {rdv.status === 'completed' && rdv.locked_at && (
                        <NoShowCountdown lockedAt={rdv.locked_at} />
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle} align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        {canCancel(rdv) && (
                          <Button
                            size="small"
                            onClick={() => {
                              setCancelError('');
                              setCancelDialog(rdv);
                            }}
                            sx={{
                              backgroundColor: '#FFF3E0',
                              color: '#E65100',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 13,
                              '&:hover': { backgroundColor: '#FFE0B2' },
                            }}
                          >
                            Annuler
                          </Button>
                        )}
                        {canReportNoShow(rdv) && (
                          <Button
                            size="small"
                            onClick={() => setNoShowDialog(rdv)}
                            sx={{
                              backgroundColor: '#FFEBEE',
                              color: '#C62828',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 13,
                              '&:hover': { backgroundColor: '#FFCDD2' },
                            }}
                          >
                            No-show
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#464646' }}>
                    Aucun rendez-vous pour le moment
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.totalItems ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page"
          sx={{ borderTop: '1px solid #E0E0E0' }}
        />
      </Paper>

      <Dialog
        open={!!noShowDialog}
        onClose={() => setNoShowDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>Signaler un no-show</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#262626' }}>
              <strong>Lead :</strong> {noShowDialog?.particulier?.nom ?? noShowDialog?.lead_name ?? '—'} — {noShowDialog?.vehicule ? `${noShowDialog.vehicule.marque} ${noShowDialog.vehicule.modele}` : noShowDialog?.lead_vehicle ?? '—'}
            </Typography>
          </Box>
          <TextField
            label="Motif du no-show"
            value={noShowMotif}
            onChange={(e) => setNoShowMotif(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoShowDialog(null)} sx={{ color: '#464646', textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={handleNoShow}
            disabled={!noShowMotif.trim() || reportNoShow.isPending}
            sx={{
              backgroundColor: '#C62828',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#B71C1C' },
            }}
          >
            {reportNoShow.isPending ? 'Envoi...' : 'Confirmer le no-show'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!cancelDialog}
        onClose={() => setCancelDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>Annuler le rendez-vous</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#262626' }}>
              <strong>Lead :</strong> {cancelDialog?.particulier?.nom ?? '—'} — {cancelDialog?.vehicule ? `${cancelDialog.vehicule.marque} ${cancelDialog.vehicule.modele}` : '—'}
            </Typography>
          </Box>
          <TextField
            label="Motif de l'annulation"
            value={cancelMotif}
            onChange={(e) => setCancelMotif(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            helperText="10 caractères minimum"
            error={cancelMotif.length > 0 && cancelMotif.trim().length < 10}
          />
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialog(null)} sx={{ color: '#464646', textTransform: 'none' }}>
            Retour
          </Button>
          <Button
            onClick={handleCancel}
            disabled={cancelMotif.trim().length < 10 || cancelRdv.isPending}
            sx={{
              backgroundColor: '#E65100',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#BF360C' },
            }}
          >
            {cancelRdv.isPending ? 'Annulation...' : "Confirmer l'annulation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Rdvs;
